import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MEDIA_TYPE_NAMES } from '../../types';

interface ExerciseData {
  day: number;
  mediaType: 0 | 1 | 2 | 3 | 4 | 5;
  title: string;
  instructions: string;
  mediaUrl: string;
}

interface ExerciseEditorProps {
  day: number;
  exercise: ExerciseData;
  onChange: (day: number, exercise: ExerciseData) => void;
  isOpen: boolean;
  onToggle: () => void;
}

const ExerciseEditor: React.FC<ExerciseEditorProps> = ({
  day,
  exercise,
  onChange,
  isOpen,
  onToggle
}) => {
  const [localExercise, setLocalExercise] = useState<ExerciseData>(exercise);

  useEffect(() => {
    setLocalExercise(exercise);
  }, [exercise]);

  const handleChange = (field: keyof ExerciseData, value: any) => {
    const updated = { ...localExercise, [field]: value };
    setLocalExercise(updated);
    onChange(day, updated);
  };

  const getMediaTypeDescription = (mediaType: number) => {
    switch (mediaType) {
      case 0: return 'Students will submit files after completing written instructions';
      case 1: return 'Provide a YouTube, Vimeo, or other video URL';
      case 2: return 'Written instructions only, no file submission required';
      case 3: return 'Link to a Google Slides presentation';
      case 4: return 'Link to a Google Doc';
      case 5: return 'Any website or online resource URL';
      default: return '';
    }
  };

  const requiresUrl = (mediaType: number) => {
    return [1, 3, 4, 5].includes(mediaType);
  };

  const isComplete = () => {
    if (!localExercise.title.trim()) return false;
    if (!localExercise.instructions.trim()) return false;
    if (requiresUrl(localExercise.mediaType) && !localExercise.mediaUrl.trim()) return false;
    return true;
  };

  return (
    <div className="border border-powder-200 rounded-lg mb-4">
      {/* Day Header */}
      <button
        onClick={onToggle}
        className={`w-full p-4 flex items-center justify-between transition-colors ${
          isOpen ? 'bg-sage-50' : 'bg-white hover:bg-powder-50'
        }`}
      >
        <div className="flex items-center space-x-3">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
            isComplete() ? 'bg-green-500' : 'bg-gray-400'
          }`}>
            {day}
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-powder-800">
              Day {day} - {localExercise.title || 'Untitled Exercise'}
            </h3>
            <p className="text-sm text-powder-600">
              {MEDIA_TYPE_NAMES[localExercise.mediaType as keyof typeof MEDIA_TYPE_NAMES]}
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          {isComplete() && (
            <span className="text-green-600 text-sm font-medium">Complete</span>
          )}
          <svg 
            className={`w-5 h-5 text-powder-600 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Exercise Details */}
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          className="p-4 border-t border-powder-200 bg-white space-y-4"
        >
          {/* Media Type Selector */}
          <div>
            <label className="block text-sm font-medium text-powder-700 mb-2">
              Exercise Type
            </label>
            <select
              value={localExercise.mediaType}
              onChange={(e) => handleChange('mediaType', parseInt(e.target.value) as 0 | 1 | 2 | 3 | 4 | 5)}
              className="w-full px-3 py-2 border border-powder-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sage-500"
            >
              {Object.entries(MEDIA_TYPE_NAMES).map(([value, name]) => (
                <option key={value} value={value}>
                  {name}
                </option>
              ))}
            </select>
            <p className="text-xs text-powder-500 mt-1">
              {getMediaTypeDescription(localExercise.mediaType)}
            </p>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-powder-700 mb-2">
              Exercise Title *
            </label>
            <input
              type="text"
              value={localExercise.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="Enter a clear, descriptive title"
              className="w-full px-3 py-2 border border-powder-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sage-500"
            />
          </div>

          {/* Instructions */}
          <div>
            <label className="block text-sm font-medium text-powder-700 mb-2">
              Instructions *
            </label>
            <textarea
              value={localExercise.instructions}
              onChange={(e) => handleChange('instructions', e.target.value)}
              placeholder="Provide clear instructions for students..."
              rows={4}
              className="w-full px-3 py-2 border border-powder-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sage-500 resize-vertical"
            />
          </div>

          {/* Media URL (if required) */}
          {requiresUrl(localExercise.mediaType) && (
            <div>
              <label className="block text-sm font-medium text-powder-700 mb-2">
                {localExercise.mediaType === 1 ? 'Video URL' : 
                 localExercise.mediaType === 3 ? 'Google Slides URL' :
                 localExercise.mediaType === 4 ? 'Google Doc URL' : 'Website URL'} *
              </label>
              <input
                type="url"
                value={localExercise.mediaUrl}
                onChange={(e) => handleChange('mediaUrl', e.target.value)}
                placeholder={
                  localExercise.mediaType === 1 ? 'https://www.youtube.com/watch?v=...' :
                  localExercise.mediaType === 3 ? 'https://docs.google.com/presentation/...' :
                  localExercise.mediaType === 4 ? 'https://docs.google.com/document/...' :
                  'https://example.com/resource'
                }
                className="w-full px-3 py-2 border border-powder-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sage-500"
              />
            </div>
          )}

          {/* Validation Messages */}
          {!isComplete() && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <div className="flex items-start space-x-2">
                <svg className="w-5 h-5 text-yellow-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <div>
                  <p className="text-yellow-800 text-sm font-medium">Missing required fields:</p>
                  <ul className="text-yellow-700 text-sm mt-1 space-y-1">
                    {!localExercise.title.trim() && <li>• Exercise title is required</li>}
                    {!localExercise.instructions.trim() && <li>• Instructions are required</li>}
                    {requiresUrl(localExercise.mediaType) && !localExercise.mediaUrl.trim() && <li>• URL is required for this exercise type</li>}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
};

export default ExerciseEditor;